import React, { useState } from 'react';
import { Modal } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import {  getStatusColor } from '../utils/statusHelpers';
import styles from './modals.module.scss';

interface StatusChangeModalProps {
  visible: boolean;
  onCancel: () => void;
  onConfirm: (newStatus: string, changeSubentries: boolean) => void;
  currentStatus: string;
  hasSubentries: boolean;
  loading?: boolean;
  selectedStatus?: string; // FooterActions'tan gelen seçili statü
}

const StatusChangeModal: React.FC<StatusChangeModalProps> = ({
  visible,
  onCancel,
  onConfirm,
  currentStatus,
  hasSubentries,
  loading = false,
  selectedStatus: initialSelectedStatus
}) => {
  const [selectedStatus, setSelectedStatus] = useState<string>(initialSelectedStatus || 'NeedsReview');
  const [changeSubentries, setChangeSubentries] = useState<boolean>(false);

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'NeedsReview':
        return 'Needs Review';
      case 'Updated':
        return 'Updated';
      case 'Approved':
        return 'Approved';
      default:
        return status;
    }
  };

  const handleConfirm = () => {
    onConfirm(selectedStatus, changeSubentries);
  };

  // Modal açıldığında seçili statüyü güncelle
  React.useEffect(() => {
    if (visible && initialSelectedStatus) {
      setSelectedStatus(initialSelectedStatus);
    }
  }, [visible, initialSelectedStatus]);

  return (
    <Modal
      centered
      title={
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span className={styles.modalTitle}>Change Status</span>
          <CloseOutlined 
            onClick={onCancel}
            style={{ cursor: 'pointer', color: '#F6FFED', fontSize: '16px' }}
          />
        </div>
      }
      open={visible}
      onCancel={onCancel}
      onOk={handleConfirm}
      confirmLoading={loading}
      okText="Change Status"
      cancelText="Cancel"
      footer={null}
      className={styles.modal}
      width={600}
    >
      <div style={{ padding: '24px' }}>
        {/* Current Status */}
        <div style={{ marginBottom: '24px' }}>
          <p style={{ marginBottom: '12px', fontWeight: '600', color: '#F6FFED', fontSize: '14px' }}>
            Current Status
          </p>
          <div style={{ 
            padding: '12px 16px', 
            background: 'rgba(255, 255, 255, 0.1)', 
            borderRadius: '8px',
            border: '1px solid rgba(255, 255, 255, 0.2)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ color: '#F6FFED', fontWeight: '500' }}>
                {getStatusLabel(currentStatus)}
              </span>
            </div>
          </div>
        </div>

        {/* New Status Selection */}
        <div style={{ marginBottom: '24px' }}>
          <p style={{ marginBottom: '12px', fontWeight: '600', color: '#F6FFED', fontSize: '14px' }}>
            Selected New Status
          </p>
          <div style={{
            padding: '12px 16px',
            background: 'var(--app-bootstrap-bg)',
            border: '1px solid var(--app-accent-color)',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <div style={{
              width: '12px',
              height: '12px',
              borderRadius: '50%',
              backgroundColor: getStatusColor(selectedStatus)
            }} />
            <span style={{ color: '#F6FFED', fontWeight: '500' }}>
              {getStatusLabel(selectedStatus)}
            </span>
          </div>
        </div>

        {/* Subentries Option - Only for items with subentries */}
        {hasSubentries && (
          <div style={{ marginBottom: '24px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
              <input
                type="checkbox"
                id="changeSubentriesDirect"
                checked={changeSubentries}
                onChange={(e) => setChangeSubentries(e.target.checked)}
                style={{ 
                  width: '16px', 
                  height: '16px',
                  accentColor: 'var(--app-accent-color)'
                }}
              />
              <label htmlFor="changeSubentriesDirect" style={{ color: '#F6FFED', fontSize: '14px' }}>
                Change status of sub-items as well
              </label>
            </div>
            <p style={{ fontSize: '12px', color: '#999', marginLeft: '28px' }}>
              If this option is checked, the status of all files, folders, versions and sub-items under the selected item will also be changed.
            </p>
          </div>
        )}

        {/* Action Buttons */}
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '24px' }}>
          <button
            onClick={onCancel}
            style={{
              padding: '8px 16px',
              background: 'transparent',
              border: '1px solid #666',
              color: '#F6FFED',
              borderRadius: '20px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={loading}
            style={{
              padding: '8px 16px',
              background: 'var(--app-accent-color)',
              border: '1px solid var(--app-accent-color)',
              color: '#F6FFED',
              borderRadius: '20px',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              opacity: loading ? 0.6 : 1
            }}
          >
            {loading ? 'Changing...' : 'Change Status'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default StatusChangeModal; 